import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EstRolloverService } from './est-rollover.service';
import { EstQtrMaintenanceService } from '../est-qtr-maintenance/est-qtr-maintenance.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-est-rollover',
    templateUrl: './est-rollover.component.html',
    styleUrls: ['./est-rollover.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EstRolloverComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isLoading = false;

  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  showFormLoader = true;
  showBackdrop = false;
  rollovers: any[] = [];
  taxYearList: any[] = [];
  outTaxYearList: any[] = [];
  quarterList: any[] = [];
  fromQuarterList: any[] = [];
  toQuarterList: any[] = [];
  showRolloverConfirmModal = false;
  rolloverConfirmConfig: ConfirmationModalConfig = {
    title: 'Rollover',
    message: 'Are you sure you want to rollover?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true
  };
  private pendingRolloverItem: any = null;
  private pendingRolloverIndex = -1;

  rollover: any = {
    tax_year: '',
    out_tax_year: '',
    from_qtr: '',
    to_qtr: '',
    rollover_status: 0
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private estRolloverService: EstRolloverService,
    private estQtrMaintenanceService: EstQtrMaintenanceService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.onTabActivation();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
    }));
    this.onInitialRender();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private onInitialRender(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    if (activeScreen?.data && Array.isArray(activeScreen.data)) {
      this.ensureQuarterListForDisplay(activeScreen.data);
      this.showBackdrop = false;
      this.showFormLoader = false;
    } else {
      // Legacy parity: do not auto-fetch on page load.
      this.showBackdrop = true;
      this.showFormLoader = false;
    }
    this.cdr.detectChanges();
  }

  private onTabActivation(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    if (!this.isEstRolloverScreen(activeScreen)) {
      return;
    }

    // Legacy parity: data should load only when user clicks Filter.
    if (activeScreen?.loading_data === true) {
      this.loadAllDataAfterFilterClick();
      return;
    }

    if (activeScreen?.data && Array.isArray(activeScreen.data)) {
      this.ensureQuarterListForDisplay(activeScreen.data);
      this.showBackdrop = false;
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private isEstRolloverScreen(activeScreen: any): boolean {
    const link = String(activeScreen?.link || '').toLowerCase();
    return link.includes('est-rollover') || link.includes('est-quarter-rollover');
  }

  private buildCommonParams(): any {
    return {
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || '',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '',
      jcd_key: this.rowData?.JCD_KEY || this.globalService?.jcd_key || 250,
      out_year: this.filterParams?.out_year || '',
      workflow: this.filterParams?.wf_name || ''
    };
  }

  private loadAllDataAfterFilterClick(): void {
    if (this.isLoading || !this.GENERAL_CONFIG?.base_url) {
      return;
    }
    this.isLoading = true;
    this.rollovers = [];
    this.toQuarterList = [];
    this.fromQuarterList = [];
    this.showFormLoader = true;
    this.showBackdrop = false;
    this.cdr.detectChanges();

    const params = this.buildCommonParams();

    this.estQtrMaintenanceService.getQuarterList(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (quarterResp: any) => {
        this.quarterList = this.normalizeQuarterList(quarterResp?.jsonObject);

        this.estRolloverService.getLockedQuarters(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (lockedResp: any) => {
            const lockedList = Array.isArray(lockedResp?.jsonObject) ? lockedResp.jsonObject : [];
            const lockedQuarterValues = new Set(
              lockedList
                .map((locked: any) => this.extractQuarterValue(locked))
                .filter((value: string) => value.length > 0)
            );
            this.fromQuarterList = this.quarterList.filter((quarter: any) =>
              lockedQuarterValues.has(String(quarter?.VALUE ?? quarter?.value ?? '').trim())
            );

            this.estRolloverService.getRolloverData(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
              (rolloverResp: any) => {
                const rows = Array.isArray(rolloverResp?.jsonObject) ? rolloverResp.jsonObject : [];
                this.hydrateFromCachedRows(rows);
                const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
                activeScreen.data = rows;
                activeScreen.loading_data = false;
                this.showFormLoader = false;
                this.isLoading = false;
                this.cdr.detectChanges();
              },
              (rolloverError: any) => {
                console.error('Error loading rollover data:', rolloverError);
                this.finishLoadWithError();
              }
            );
          },
          (lockedError: any) => {
            console.error('Error loading locked quarter data:', lockedError);
            this.finishLoadWithError();
          }
        );
      },
      (quarterError: any) => {
        console.error('Error loading quarter list:', quarterError);
        this.finishLoadWithError();
      }
    );
  }

  private finishLoadWithError(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    activeScreen.loading_data = false;
    this.showFormLoader = false;
    this.isLoading = false;
    this.gtwSnackbarService.openSnackBar('Error loading EST rollover data', 'gtw-snackbar--red');
    this.cdr.detectChanges();
  }

  private hydrateFromCachedRows(rows: any[]): void {
    this.rollovers = rows.map((row: any, index: number) => {
      const fromValue = row?.from_quarter ?? row?.from_qtr ?? row?.FROM_QUARTER ?? row?.FROM_QTR ?? '';
      const toValue = row?.to_quarter ?? row?.to_qtr ?? row?.TO_QUARTER ?? row?.TO_QTR ?? '';
      const fromQuarterObj = this.findQuarterOption(fromValue);
      const toQuarterObj = this.findQuarterOption(toValue);
      const rolloverState =
        row?.rollover_status
        ?? row?.ROLLOVER_STATUS
        ?? row?.rollover_y_n
        ?? row?.ROLLOVER_Y_N
        ?? 0;
      this.ensureToQuarterOptions(fromQuarterObj ?? fromValue, index);
      return {
        tax_year: row?.tax_year ?? row?.TAX_YEAR ?? '',
        out_tax_year: row?.out_year ?? row?.out_tax_year ?? row?.OUT_YEAR ?? row?.OUT_TAX_YEAR ?? '',
        from_qtr: fromQuarterObj ?? fromValue,
        to_qtr: toQuarterObj ?? toValue,
        rollover_status:
          rolloverState === 'Y'
          || rolloverState === 1
          || String(rolloverState).toUpperCase() === 'Y'
            ? 1
            : 0
      };
    });
  }

  private ensureToQuarterOptions(selectedQuarter: any, index: number): void {
    const selectedNum = Number(this.extractQuarterValue(selectedQuarter));
    const toQuarters = this.quarterList.filter((item: any) => Number(item?.VALUE ?? item?.value) > selectedNum);
    this.toQuarterList[index] = toQuarters;
  }

  private normalizeQuarterList(list: any): any[] {
    if (!Array.isArray(list)) {
      return [];
    }
    return list.map((item: any) => {
      const value = String(item?.VALUE ?? item?.value ?? '');
      const label = item?.LABEL ?? item?.label;
      if (value === '0' && (label === undefined || label === null || String(label).trim() === '')) {
        return { ...item, LABEL: 'Undefined' };
      }
      return item;
    });
  }

  private findQuarterOption(value: any): any {
    const normalized = this.extractQuarterValue(value);
    if (!normalized) {
      return null;
    }
    return this.quarterList.find((q: any) => String(q?.VALUE ?? q?.value ?? '') === normalized) || null;
  }

  private ensureQuarterListForDisplay(rows: any[]): void {
    if (!Array.isArray(rows)) {
      this.rollovers = [];
      return;
    }

    if (this.quarterList.length > 0 && this.fromQuarterList.length > 0) {
      this.hydrateFromCachedRows(rows);
      return;
    }

    if (!this.GENERAL_CONFIG?.base_url) {
      this.hydrateFromCachedRows(rows);
      return;
    }

    const params = this.buildCommonParams();
    this.estQtrMaintenanceService.getQuarterList(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (quarterResp: any) => {
        this.quarterList = this.normalizeQuarterList(quarterResp?.jsonObject);
        this.estRolloverService.getLockedQuarters(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (lockedResp: any) => {
            const lockedList = Array.isArray(lockedResp?.jsonObject) ? lockedResp.jsonObject : [];
            const lockedQuarterValues = new Set(
              lockedList
                .map((locked: any) => this.extractQuarterValue(locked))
                .filter((value: string) => value.length > 0)
            );
            this.fromQuarterList = this.quarterList.filter((quarter: any) =>
              lockedQuarterValues.has(String(quarter?.VALUE ?? quarter?.value ?? '').trim())
            );
            this.hydrateFromCachedRows(rows);
            this.cdr.detectChanges();
          },
          () => {
            this.hydrateFromCachedRows(rows);
            this.cdr.detectChanges();
          }
        );
      },
      () => {
        this.hydrateFromCachedRows(rows);
        this.cdr.detectChanges();
      }
    );
  }

  compareQuarterByValue = (a: any, b: any): boolean => {
    const av = String(a?.VALUE ?? a?.value ?? a ?? '');
    const bv = String(b?.VALUE ?? b?.value ?? b ?? '');
    return av === bv;
  };

  private extractQuarterValue(source: any): string {
    if (source === null || source === undefined) {
      return '';
    }

    if (typeof source !== 'object') {
      const rawPrimitive = String(source).trim();
      if (!rawPrimitive) {
        return '';
      }
      if (/^\d+$/.test(rawPrimitive)) {
        return rawPrimitive;
      }
      const primitiveLower = rawPrimitive.toLowerCase();
      if (primitiveLower.includes('extension')) {
        return '5';
      }
      const primitiveQuarterNum = primitiveLower.match(/quarter\s*([0-9]+)/)?.[1] || primitiveLower.match(/\b([1-5])\b/)?.[1];
      return primitiveQuarterNum ? primitiveQuarterNum : '';
    }

    const direct =
      source?.QTR
      ?? source?.qtr
      ?? source?.Qtr
      ?? source?.VALUE
      ?? source?.value
      ?? source?.quarter
      ?? source?.QUARTER
      ?? source?.from_qtr
      ?? source?.FROM_QTR
      ?? source?.from_quarter
      ?? source?.FROM_QUARTER
      ?? source?.LABEL
      ?? source?.label
      ?? '';

    const raw = String(direct).trim();
    if (!raw) {
      return '';
    }
    if (/^\d+$/.test(raw)) {
      return raw;
    }

    const lower = raw.toLowerCase();
    if (lower.includes('extension')) {
      return '5';
    }
    const quarterNum = lower.match(/quarter\s*([0-9]+)/)?.[1] || lower.match(/\b([1-5])\b/)?.[1];
    return quarterNum ? quarterNum : '';
  }

  addRollover() {
    const newRow = {
      tax_year: this.filterParams?.tax_year || '',
      out_tax_year: this.filterParams?.out_year || '',
      from_qtr: null,
      to_qtr: null,
      rollover_status: 0
    };
    this.rollovers.push(newRow);
    this.ensureToQuarterOptions(newRow.from_qtr, this.rollovers.length - 1);
    this.cdr.detectChanges();
  }

  deleteRollover(index: number) {
    this.rollovers.splice(index, 1);
    this.cdr.detectChanges();
  }

  saveRollover(rollover: any, index: number) {
    const fromQtr = rollover?.from_qtr?.VALUE ?? rollover?.from_qtr;
    const toQtr = rollover?.to_qtr?.VALUE ?? rollover?.to_qtr;
    if (!fromQtr || !toQtr) {
      rollover.rollover_status = 0;
      this.gtwSnackbarService.openSnackBar('Please select From Quarter and To Quarter', 'gtw-snackbar--yellow');
      this.cdr.detectChanges();
      return;
    }
    const params = {
      tax_year: rollover?.tax_year || this.filterParams?.tax_year || this.globalService?.tax_year,
      scenario: this.filterParams?.scenario || this.globalService?.scenario,
      jcd_key: this.rowData?.JCD_KEY || this.globalService?.jcd_key || 250,
      from_qtr: fromQtr,
      to_qtr: toQtr,
      out_year: rollover?.out_tax_year || this.filterParams?.out_year,
      workflow: this.filterParams?.wf_name || ''
    };

    this.estRolloverService.saveRollover(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data?.callSuccess === '1' && data?.errorMessage === 'null') {
          this.gtwSnackbarService.openSnackBar('Rollover saved successfully', 'gtw-snackbar--green');
          this.loadAllDataAfterFilterClick();
          this.cdr.detectChanges();
        } else {
          this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Error saving rollover', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.gtwSnackbarService.openSnackBar('Error saving rollover', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  onFromQuarterChange(rollover: any, index: number): void {
    this.ensureToQuarterOptions(rollover?.from_qtr, index);
    const validToOptions = this.toQuarterList[index] || [];
    const currentTo = rollover?.to_qtr?.VALUE ?? rollover?.to_qtr;
    const stillValid = validToOptions.some((opt: any) => String(opt?.VALUE ?? opt?.value) === String(currentTo));
    if (!stillValid) {
      rollover.to_qtr = null;
    }
    this.cdr.detectChanges();
  }

  onRolloverToggle(event: Event, rollover: any, index: number): void {
    const checked = !!(event.target as HTMLInputElement)?.checked;
    if (!checked) {
      rollover.rollover_status = 0;
      this.cdr.detectChanges();
      return;
    }

    const fromQtr = rollover?.from_qtr?.VALUE ?? rollover?.from_qtr;
    const toQtr = rollover?.to_qtr?.VALUE ?? rollover?.to_qtr;
    if (!fromQtr || !toQtr) {
      rollover.rollover_status = 0;
      this.gtwSnackbarService.openSnackBar('Please select From Quarter and To Quarter', 'gtw-snackbar--yellow');
      this.cdr.detectChanges();
      return;
    }

    // Prevent duplicate quarter pairs in the current list.
    const duplicate = this.rollovers.some((r: any, i: number) => {
      if (i === index) return false;
      const rf = r?.from_qtr?.VALUE ?? r?.from_qtr;
      const rt = r?.to_qtr?.VALUE ?? r?.to_qtr;
      return String(rf) === String(fromQtr) && String(rt) === String(toQtr);
    });
    if (duplicate) {
      rollover.rollover_status = 0;
      this.gtwSnackbarService.openSnackBar('Selected quarters are already roll-forwarded', 'gtw-snackbar--yellow');
      this.cdr.detectChanges();
      return;
    }

    this.pendingRolloverItem = rollover;
    this.pendingRolloverIndex = index;
    rollover.rollover_status = 1;
    this.showRolloverConfirmModal = true;
    this.cdr.detectChanges();
  }

  onConfirmRollover(): void {
    if (!this.pendingRolloverItem || this.pendingRolloverIndex < 0) {
      this.showRolloverConfirmModal = false;
      return;
    }
    this.showRolloverConfirmModal = false;
    this.saveRollover(this.pendingRolloverItem, this.pendingRolloverIndex);
    this.pendingRolloverItem = null;
    this.pendingRolloverIndex = -1;
  }

  onCancelRollover(): void {
    if (this.pendingRolloverItem) {
      this.pendingRolloverItem.rollover_status = 0;
    }
    this.showRolloverConfirmModal = false;
    this.pendingRolloverItem = null;
    this.pendingRolloverIndex = -1;
    this.cdr.detectChanges();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
